import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate, useParams} from "react-router";
import {
    Box,
    Button,
    Checkbox,
    Flex,
    IconButton,
    Input,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useBreakpointValue,
    useToast,
    VStack,
} from "@chakra-ui/react";
import {useAppSelector} from "../../redux/Store";
import {ContentTypeUtils, Product} from "../../shared/entities/Product/Product";
import {ListProductItemConfig, ListProductItemRow} from "../../components/List/ListProductItem";
import {Loader} from "../../components/Loader/Loader";
import {MainButton} from "../../components/Buttons/MainButton";
import {OrderController} from "../../controllers/OrderController";
import {ModalService} from "../../service/Modal/ModalService";
import {CheckoutRequest} from "../../shared/entities/Client/CheckoutRequest";
import {URLPaths} from "../../config/application/URLPaths";
import {BoxStyleConfig} from "../Forms/FormStyleConfigs";
import {format, parse} from "date-fns";
import {Breadcrumbs} from "../../components/Breadcrumbs/Breadcrumbs";
import {TrashItem} from "../../components/Inputs/Icons/Icons";
import ImageCustom from "../../components/Image/ImageCustom";
import QuantityInput from "../../components/Inputs/QuantityInput";
import {CloseIcon, Icon} from "@chakra-ui/icons";
import {UserController} from "../../controllers/UserController";
import {Dealership, DealershipMinInfo} from "../../shared/entities/Users/Dealership";
import {ProductTypes} from "../../common/constants";
import {FiCheck, FiEdit, FiX} from "react-icons/fi";
import {ProductCostCalculateUtils} from "../../shared/utilities/ProductCostCalculateUtils";
import {calculateStripePercentFeeAmount, getStripeCommissionLabel} from "../../shared/utils/stripeCommission";
import {useRoVinConflictCheck} from "./hooks/useRoVinConflictCheck";
import {RoVinConflictModal} from "./RoVinConflictModal";
import {isUserAllowedCardPayment} from "../../shared/utils/cardPaymentAccess";
import DiscountComponent from "../Products/Discount/DiscountComponent";
import {ProductController} from "../../controllers/ProductController";
import {CompareDiscountObject, CompareObject, OrderTypes, PaymentTypes} from "../../shared/entities/Order/Order";

import IconWithTooltip from "../../components/Icon/IconWithTooltip";
import {AuthorizationForm} from "../Forms/Authoization/AuthorizationForm";
import {RequestRegistrationForm} from "../Forms/Clients/RequestRegistrationForm";
import {CartService} from "../../service/Cart/CartService";
import {BreadcrumbsData} from "../../shared/entities/Breadcrumbs/BreadCrumbsData";
import {hasPermission, isRoleAdmin} from "../../features/Permission/AllowedAccess";
import {
    dealershipExcludeSelectPermission,
    orderEditPermission,
    orderViewPermission
} from "../permissions";
import {setBreadcrumbsLinks} from "../../redux/Breadcrumbs/BreadcrumbsReducer";
import {ScheduledShipmentController} from "../../controllers/ScheduledShipmentController";
import {mapShipments, ScheduledShipment} from "../../shared/entities/Order/ScheduledShipment";
import OrderPaperPopover from "../Modal/OrderPaperPopover";
import {FaWindowClose} from "react-icons/fa";
import {StripePaymentModal} from "../Modal/StripePaymentModal";

function parseOrderBasketViewDate(dateString: any) {
    const parsedDate = parse(dateString, 'MM.dd.yyyy HH:mm', new Date());
    if (!parsedDate) {
        throw new RangeError('Invalid time value');
    }
    return parsedDate;
}

export const OrderBasket: FC<{ account?: boolean, links?: Array<BreadcrumbsData> }> = ({account = false, links}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const toast = useToast()
    const [isLoading, setIsLoading] = useState(true)
    const orderController = new OrderController(dispatch);
    const productController = new ProductController(dispatch);
    const {id} = useParams();
    const modalService = new ModalService(dispatch)
    const cartService = new CartService(dispatch)
    const isUnRegistered = location.pathname.includes('unregistered');
    const isViewWithPath = location.pathname.includes('view') || isUnRegistered;
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [shippingCost, setShippingCost] = useState<number>(0);
    const [paymentState, setPaymentState] = useState<OrderTypes>(0);
    const [subTotal, setSubTotal] = useState<number>(0);
    const [tax, setTax] = useState<number>(0);
    const [updateDealer, setUpdateDealer] = useState<boolean>(false);
    const [dateOrder, setDate] = useState<string>("");
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [poNumber, setPoNumber] = useState("");
    const [confirmNumber, setConfirmNumber] = useState<string>("");
    const [namePo, setNamePo] = useState("");
    const [updatedProductList, setProductList] = useState<Array<Product>>([]);
    const [selectedDealer, setSelectedDealer] = useState<Dealership | undefined>();
    const [dealershipInputValue, setDealershipInputValue] = useState("");
    const [isDealershipDropdownOpen, setIsDealershipDropdownOpen] = useState(false);
    const [dealershipDropdownPlacement, setDealershipDropdownPlacement] = useState<"up" | "down">("down");
    const [dealershipDropdownMaxH, setDealershipDropdownMaxH] = useState("50vh");
    const dealershipSelectRef = useRef<HTMLDivElement>(null);
    const [shippingEdit, setShippingEdit] = useState<Boolean>(true);
    const userController = new UserController(dispatch);
    const [refresh, setRefresh] = useState(false);
    const order = useAppSelector(store => store?.OrderReducer?.order)
    const currentState = useAppSelector(state => state.StatesReducer.currentState);
    const scheduledList = useAppSelector(state => state.ScheduledShipmentReducer.list);
    const cartItems = useAppSelector((state) => state.CartReducer.cartItems);
    const authDealership = useAppSelector(state => state.AuthReducer.dealership);

    const selDealership = useAppSelector(state => state.UserReducer.dealership);
    const currentUser = useAppSelector(store => store.AuthReducer.user);
    const paymentConfigLoaded = useAppSelector((s) => s.PaymentConfigReducer?.loaded === true);
    const allowCardPayment = isUserAllowedCardPayment(currentUser);
    const dealershipsMinInfo = useAppSelector(state => state.UserReducer.dealershipsMinInfo);
    const paperOrder = useAppSelector(store => store?.OrderReducer?.paperOrder)
    let isMobile = useBreakpointValue({base: true, sm: true, md: true, lg: false, xl: false, "2xl": false})
    const [showStripePayment, setShowStripePayment] = useState(false);
    const [stripeOrderId, setStripeOrderId] = useState<string | undefined>();
    const {
        detectedVins: cartNamePoVins,
        ackedForCurrentPo: roVinAckedForCurrentPo,
        shouldBlockCheckout: roVinBlocksCheckout,
        roVinConflicts,
        roVinConflictOrderIds,
        roVinConflictRows,
        roVinCheckLoading,
        roVinModalOpen,
        acknowledgeCurrentPo: handleRoVinModalContinue,
        closeModal: handleRoVinModalClose,
    } = useRoVinConflictCheck({
        namePo,
        selectedDealerId: selectedDealer?.id,
        isViewWithPath,
        checkNamePoVins: orderController.checkNamePoVins.bind(orderController),
    });

    const calculateTotalPrice = useCallback((updatedProductList: Array<Product>, dealer: Dealership | undefined): void => {
        const result = ProductCostCalculateUtils.calculateTotalPrice(updatedProductList, dealer, selectedDealer ? undefined : currentState ? currentState.tax : undefined);
        setTotalPrice(result.totalPrice);
        setShippingCost(result.shippingCost);
        setSubTotal(result.subtotal);
        setTax(result.taxCost);
        console.log("calculateTotalPrice", result);
    }, [selectedDealer, currentState]);

    useEffect(() => {
        if (links) {
            setBreadcrumbsLinks(dispatch, links);
        }
    }, [links, dispatch]);

    useEffect(() => {
        if (authDealership) {
            setSelectedDealer((hasPermission(orderEditPermission) || hasPermission(dealershipExcludeSelectPermission)) && updateDealer ? selDealership : authDealership)
        }
    }, [authDealership, selDealership]);

    useEffect(() => {
        const scheduledShipmentController = new ScheduledShipmentController(dispatch);
        scheduledShipmentController.clearListShipment();
        if (!isViewWithPath) {
            if (selectedDealer && JSON.stringify(selectedDealer) !== '{}' && cartItems.length !== 0) {
                scheduledShipmentController.all({data: {dealershipId: selectedDealer?.id}}).then(res => {
                    setIsLoading(false)
                })

                cartService.updateDealershipCart(selectedDealer);
                productController
                    .checkBasket({cartItems: cartItems, dealershipId: selectedDealer?.id})
                    .then((res) => {
                        if (res.Some) {
                            const {outOffDateProduct, outOfDateDiscount, items} = res.Some;
                            const productMap = new Map(
                                cartItems.map((product: Product) => [product.id, product.name])
                            );
                            let productChanges: JSX.Element[] = [];
                            let discountChanges: JSX.Element[] = [];
                            let removedProducts: JSX.Element[] = [];

                            const currentProductIds = new Set(cartItems.map((product: Product) => product.id));
                            const newProductIds = new Set(items.map((product: Product) => product.id));

                            // Определяем удаленные продукты (которые были в корзине, но отсутствуют в новом списке)
                            const removedProductIds = [...currentProductIds].filter(id => !newProductIds.has(id));

                            if (removedProductIds.length > 0) {
                                removedProducts = removedProductIds.map(productId => {
                                    const productName = productMap.get(productId) || 'Unknown Product'
                                    return (
                                        <Box key={productId} pt={1}>
                                            <strong>{String(productName)}</strong> is no longer available for sale.
                                        </Box>
                                    );
                                });
                            }
                            if (outOffDateProduct) {
                                // Формируем информацию для отображения
                                productChanges = Object.entries(outOffDateProduct).map(([productId, changes]: [string, any]) => {
                                    const productName = productMap.get(parseInt(productId)) || 'Unknown Product';

                                    return (
                                        <Box key={productId} pt={1}>
                                            <strong> {productName}</strong>
                                            {changes.map((change: CompareObject, index: any) => (
                                                <div key={index}>
                                                    <strong>{change.field} :</strong>Out of Date: {change.outOffDate} ,
                                                    Current: {change.current}
                                                </div>
                                            ))}
                                        </Box>
                                    );
                                });
                            }
                            if (outOfDateDiscount) {
                                discountChanges = Object.entries(outOfDateDiscount).map(([productId, changes]: [string, any]) => {
                                    const productName = productMap.get(parseInt(productId)) || 'Unknown Product';
                                    return (
                                        <Box key={productId} pt={2}>
                                            <strong> {productName}</strong>
                                            {changes.map((change: CompareDiscountObject, index: any) => (
                                                <div key={index}>
                                                    <strong>{ContentTypeUtils.getCostState(change.discountType).name} :</strong>Out
                                                    of Date: {change.outOffDate}% , Current: {change.current}%
                                                </div>
                                            ))}
                                        </Box>
                                    );
                                });
                            }

                            if (productChanges.length > 0 || discountChanges.length > 0 || removedProducts.length > 0) {
                                toast({
                                    position: 'top',
                                    title: 'Some products in your cart have been updated or removed.',
                                    description: <>{removedProducts}{productChanges}{discountChanges}</> || 'Some items or discounts in your cart are outdated.',
                                    status: 'warning',
                                    duration: 8000,
                                    isClosable: true,
                                });

                                cartService.updateProductsCart(items);
                            }
                        }
                    });
            }
            setProductList(cartItems);
            calculateTotalPrice(cartItems, selectedDealer);
            setPoNumber("");
            setConfirmNumber("");
            setNamePo("");
        }
    }, [cartItems, selectedDealer, currentState, refresh, calculateTotalPrice]);

    useEffect(() => {
        setIsLoading(true);
        if (!isViewWithPath) {
            calculateTotalPrice(updatedProductList, selectedDealer);
        } else {
            orderController.getOrder(Number(id), isUnRegistered).then((order) => {
                if (order.None) {
                    toast({
                        position: "top",
                        title: 'Error receiving order,' + order?.None?.getErrorDescription() + ', try logging in',
                        status: 'error',
                        duration: 8000,
                        isClosable: true,
                    })
                } else {
                    const newUpdatedProductList: Array<Product> = [];
                    for (const elem of order.Some.products) {
                        elem.product.quantity = elem.quantity
                        elem.product.discount = elem.discount
                        elem.product.shippingCost = elem.shippingCost
                        elem.product.taxCost = elem.taxCost
                        elem.product.price = elem.price
                        elem.product.finalPrice = elem.finalPrice
                        newUpdatedProductList.push({...elem.product});
                    }
                    setProductList(newUpdatedProductList);
                    setPaymentState(order.Some.paymentStatus)
                    setTotalPrice(order.Some.totalAmount || 0);
                    setShippingCost(order.Some.shippingCost || 0);
                    setTax(order.Some.tax || 0);
                    setPoNumber(order.Some.poNumber || "");
                    setConfirmNumber(order.Some.confirmNumber || "");
                    setNamePo(order.Some.namePo || "000000");
                    setDate(format(parseOrderBasketViewDate(order.Some.createdAt), "MM/dd/yyyy"));
                }
            });
        }
        setIsLoading(false);
    }, [selectedDealer, isUnRegistered]);

    const cartRoPlaceholderForWidth =
        hasPermission(orderEditPermission) || hasPermission(dealershipExcludeSelectPermission)
            ? "OPTIONAL"
            : "MUST ENTER";

    /** Согласовано с handleCheckoutCart: без прав RO обязателен. */
    const roRequiredButEmpty =
        !(hasPermission(orderEditPermission) || hasPermission(dealershipExcludeSelectPermission)) &&
        namePo === "";

    const cartFieldSizingCh = (raw: string) => {
        const ph = cartRoPlaceholderForWidth;
        const floorCh = Math.min(35, Math.max(12, ph.length + 3));
        const len = raw.length > 0 ? raw.length : ph.length;
        const contentCh = Math.min(35, Math.max(12, len + 3));
        return Math.min(35, Math.max(contentCh, floorCh));
    };
    const poFieldSizingCh = cartFieldSizingCh(poNumber);
    const roFieldSizingCh = cartFieldSizingCh(namePo);

    const isTBD = ((["AK", "HI"].includes(selectedDealer?.shippingAddress?.state?.code ?? '') || shippingCost > 599) && isViewWithPath && paymentState === OrderTypes.IN) ||
        ((["AK", "HI"].includes(selectedDealer?.shippingAddress?.state?.code ?? '') || shippingCost > 599) && !isViewWithPath);

    /** Как в checkout: у не-админа в заказ уходит `tbd: true` — оплату картой не показываем. */
    const cardCheckoutBlockedByTbd = isTBD && !isRoleAdmin();
    const showPayWithCard =
        paymentConfigLoaded &&
        !isRoleAdmin() &&
        allowCardPayment &&
        !cardCheckoutBlockedByTbd;
    /** Card-only обычно скрывает Invoice Me; при TBD (карта недоступна) или без card на аккаунте — Invoice Me нужен. */
    const showInvoiceMeButton =
        !selectedDealer?.cardPaymentOnly ||
        cardCheckoutBlockedByTbd ||
        !allowCardPayment;

    interface ColumnConfig {
        header: string | JSX.Element;
        width?: string;
        isLink?: boolean;
        style?: object;
    }

    const columns: Array<ColumnConfig> = isMobile ? [
        {
            header: <Box>
                <Text>Product</Text>
                <Text>Item price</Text>
            </Box>,
            width: "30%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        }, {
            header: <Box>
                <Text>Info</Text>
                <Text>Qty</Text>
            </Box>,
            width: "40%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        }, {
            header: <Box>
                <Text>Total price</Text>
            </Box>,
            width: "30%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        }
    ] : [
        {
            header: "Product",
            width: hasPermission(orderEditPermission) ? "13%" : "15%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        },
        {
            header: "Info",
            width: "45%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        },
        {
            header: "Item price",
            width: hasPermission(orderEditPermission) ? "12%" : "15%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        },
        {
            header: "Qty",
            width: hasPermission(orderEditPermission) ? "10%" : "15%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        },
        {
            header: "Total price",
            width: hasPermission(orderEditPermission) ? "10%" : "auto",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        },
        (hasPermission(orderEditPermission)) && {
            header: "Shipping",
            width: "10%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        },
        (hasPermission(orderEditPermission)) && {
            header: "Tax",
            width: "10%",
            isLink: false,
            style: {flexBasis: "50%", color: "grey.400"}
        }
    ].filter(Boolean) as Array<ColumnConfig>;

    const ColumnNamesMapping: Array<ListProductItemConfig> = columns.map(
        (col: ColumnConfig) => {
            return {
                content: col.header || "",
                width: col.width || "auto",
                isLink: col.isLink || false,
                style: col.style || {},
            };
        }
    );
    const handleCheckboxChange = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
        }
    };

    const handleAddedSelectProductToCart = () => {
        setIsLoading(true);
        productController.getProductsIds({ids: selectedIds?.join(",")}).then(productList => {
            if (productList.Some) {
                productList.Some.items.map((product: any) => {
                    const existingProduct = updatedProductList.find((item) => item.id === product.id);
                    if (existingProduct) {
                        product.quantity = existingProduct.quantity;
                    }
                    cartService.addProductToCart(product);
                })
                setIsLoading(false);
            }
        });
    }

    const handleRemoveClick = (item: any) => {
        modalService.setModalData({
            onSubmit: () => {
                modalService.deleteModalData();
                cartService.removeFromCart(item)
            },
            isOpen: true,
            onOpen: () => {
            },
            onClose: () => {
                modalService.deleteModalData();
            },
            modalTextData: "Are you sure you want to delete the selected product?\n This action cannot be undone.",
            context: "action"
        });
    };

    const handleCheckoutCart = (total: number, shippingCost: number, tax: number) => {
        console.log("handleCheckoutCart", total, shippingCost, tax)
        if (!(hasPermission(orderEditPermission) || hasPermission(dealershipExcludeSelectPermission)) && namePo === "") {
            return;
        }
        if (roVinBlocksCheckout) {
            toast({
                position: "top",
                title: roVinCheckLoading ? "Checking VIN…" : "Confirm duplicate VIN",
                description: roVinCheckLoading
                    ? "Please wait for the check to finish."
                    : "This VIN appears in an order from the last 7 days. Use the dialog to continue or change the RO field.",
                status: roVinCheckLoading ? "info" : "warning",
                duration: 8000,
                isClosable: true,
            });
            return;
        }
        const loggedInUser = sessionStorage.getItem("LOGIN");
        if (!loggedInUser || !selectedDealer) {
            modalService.setModalData({
                onSubmit: () => {
                    modalService.deleteModalData();
                    if (!loggedInUser) {
                        navigate(URLPaths.AUTH.link + location.pathname);
                    }
                },
                isOpen: true,
                onOpen: () => {
                },
                onClose: () => {
                    modalService.deleteModalData();
                    navigate(-1);
                },
                modalTextData: loggedInUser && !selectedDealer ? "Selected dealership" : "\n" +
                    "Only logged in register user can place an order.\n" +
                    "\n" +
                    "800-260-5051\n" +
                    "Info@akdealerservices.com",
                context: "error"
            });
            return;
        }


        if (selectedDealer && selectedDealer.id) {
            const checkoutRequest: CheckoutRequest = {
                dealership: selectedDealer.id.toString(),
                userId: Number(currentUser?.id),
                poNumber: poNumber,
                tbd: isRoleAdmin() ? false : isTBD,
                confirmNumber: confirmNumber,
                namePo: namePo || "",
                acknowledgeNamePoVinReuse:
                    roVinAckedForCurrentPo && cartNamePoVins.length > 0 && roVinConflictOrderIds.length > 0,
                subTotal: subTotal,
                tax: tax,
                shippingCost: shippingCost,
                totalAmount: total,
                fee: 0,
                reorder: false,
                paymentType: PaymentTypes.INVOICE,
                productRequest: ProductCostCalculateUtils.getProductsRequest(updatedProductList, selectedDealer)
            };
            setIsLoading(true);
            orderController.orderCheckout(checkoutRequest).then(res => {
                setIsLoading(false);
                toast({
                    position: "top",
                    title: res.None ? 'Failed checkout' : 'Your order was received and will be processed and shipped to you shortly.',
                    status: res.None ? 'error' : 'success',
                    duration: 8000,
                    isClosable: true,
                })
                if (!res?.None) {
                    navigate(`${URLPaths.ORDER_HISTORY.link}`);
                }
            });
        } else {
            toast({
                position: "top",
                title: 'Error checkout selected dealership',
                status: 'error',
                duration: 3000,
                isClosable: true
            })
        }
    };

    const handleCardPayment = async () => {
        if (!allowCardPayment) {
            toast({
                position: "top",
                title: "Card payment unavailable",
                description: "Online card payment is not enabled for your account.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        if (roRequiredButEmpty) {
            return;
        }
        if (!selectedDealer?.id) {
            toast({
                position: "top",
                title: 'Cannot process payment',
                description: 'Dealer information is missing. Please select a dealership.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!selectedDealer || totalPrice <= 0) {
            toast({
                position: "top",
                title: 'Cannot process payment',
                description: 'Please check your cart and dealer information',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (roVinBlocksCheckout) {
            toast({
                position: "top",
                title: roVinCheckLoading ? "Checking VIN…" : "Confirm duplicate VIN",
                description: roVinCheckLoading
                    ? "Please wait for the check to finish."
                    : "This VIN appears in an order from the last 7 days. Confirm in the dialog or change the RO field.",
                status: roVinCheckLoading ? "info" : "warning",
                duration: 8000,
                isClosable: true,
            });
            return;
        }

        if (cardCheckoutBlockedByTbd) {
            toast({
                position: "top",
                title: "Card payment unavailable",
                description: "Shipping or total is TBD for this order. Use Invoice Me or contact support.",
                status: "info",
                duration: 8000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);

        // Теперь TypeScript знает, что selectedDealer.id существует
        const checkoutRequest: CheckoutRequest = {
            dealership: selectedDealer.id.toString(),
            userId: Number(currentUser?.id),
            poNumber: poNumber,
            tbd: isRoleAdmin() ? false : isTBD,
            confirmNumber: confirmNumber,
            namePo: namePo || "",
            acknowledgeNamePoVinReuse:
                roVinAckedForCurrentPo && cartNamePoVins.length > 0 && roVinConflictOrderIds.length > 0,
            subTotal: subTotal,
            tax: tax,
            shippingCost: shippingCost,
            totalAmount: totalPrice,
            fee: 0,
            reorder: false,
            paymentType: PaymentTypes.CARD,
            productRequest: ProductCostCalculateUtils.getProductsRequest(updatedProductList, selectedDealer)
        };

        orderController.orderCheckout(checkoutRequest, true).then(res => {
            setIsLoading(false);
            if (!res?.None) {
                setStripeOrderId(res.Some?.id?.toString());
                setShowStripePayment(true);
            } else {
                toast({
                    position: "top",
                    title: 'Failed to create order',
                    description: 'Please try again',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        });
    };

    const handlePaymentSuccess = () => {
        setShowStripePayment(false);
        setStripeOrderId(undefined);
        // Поведение как у обычного checkout: после успешной оплаты очищаем корзину.
        cartService.removeAllCart();
        toast({
            position: "top",
            title: 'Payment successful!',
            description: 'Your order has been confirmed.',
            status: 'success',
            duration: 5000,
            isClosable: true,
        });
        navigate(URLPaths.ORDER_HISTORY.link);
    };

    const handlePaymentError = (error: string) => {
        setShowStripePayment(false);
        setStripeOrderId(undefined);
        toast({
            position: "top",
            title: 'Payment failed',
            description: error,
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    };

    const handleCancelPayment = async () => {
        if (stripeOrderId) {
            const orderId = Number(stripeOrderId);
            if (Number.isFinite(orderId)) {
                await orderController.removeOrder(orderId);
            }
        }
        setShowStripePayment(false);
        setStripeOrderId(undefined);
    };
    const getList = (productList: Array<Product>) => {
        let shipmentMap: Map<number, ScheduledShipment> | undefined;
        if (scheduledList.items) {
            shipmentMap = mapShipments(scheduledList.items);
        }
        return productList?.map((elem: Product, idx: number) => {
            const shipment = shipmentMap?.get(elem.id);
            const ItemConfig: Array<ListProductItemConfig> = isMobile ? [
                    {
                        content:
                            <Box pb={4} width={"100%"}>
                                <Flex>
                                    <Box minWidth={"25%"}>
                                        <ImageCustom elem={elem} sizeInPercent={100} currentSize={"medium"}
                                                     sizeBackend={"COMMON"}/>
                                    </Box>
                                    <Box flexDir="column" textAlign="left" pl={3} pt={2}>
                                        <Text fontSize={16} fontWeight="md">{elem.name}</Text>
                                        {/*<Text fontWeight="500" color="gray.400"> Product#: {elem.id} </Text>*/}
                                        <Text fontSize={14} color="gray.400">{elem.description}</Text>
                                        {elem.type === ProductTypes.CUSTOM && (
                                            <Text fontWeight="600" fontSize="16px">
                                                {'This product is custom and will be billed and shipped on separate invoice.'}
                                            </Text>
                                        )}
                                        {elem.type === ProductTypes.REORDER && (
                                            <Text fontWeight="600" fontSize="16px">
                                                {'This product will be billed and shipped on separate invoice.'}
                                            </Text>
                                        )}
                                    </Box>
                                </Flex>
                                <Flex alignItems={"center"} pt={2}>
                                    <Flex minWidth={isViewWithPath ? "29%" : "32%"}>
                                        <DiscountComponent elem={elem} hiddenUOM={true} history={isViewWithPath}
                                                           fontWeightPrice={"300"}
                                                           textAlign={"left"}
                                                           selectedDealer={selectedDealer}
                                                           showDiscount={!isViewWithPath}
                                                           showTax={!isViewWithPath && hasPermission(orderEditPermission)}
                                                           showMarkup={!isViewWithPath && hasPermission(orderEditPermission)}
                                        />
                                    </Flex>
                                    <Flex w={"100%"}>
                                        {isViewWithPath ?
                                            <Text fontSize="16px" fontWeight={"md"}
                                                  width={"50%"}>{elem.quantity + " : Qty"}</Text> :
                                            <QuantityInput item={elem} isViewWithPath={isViewWithPath}
                                                           isMobile={isMobile}
                                                           width={"50%"}
                                                           onQuantityRemove={(updatedItem) => {
                                                               handleRemoveClick(updatedItem);
                                                           }}
                                                           onQuantityChange={(updatedItem) => {
                                                               const updatedProduct = cartItems.find((item) => item.id === elem.id);
                                                               if (updatedProduct) {
                                                                   updatedProduct.quantity = updatedItem.quantity;
                                                                   cartService.addProductToCart(updatedProduct);
                                                               }
                                                           }}/>
                                        }

                                        <Flex justifyContent={"space-between"} w={"50%"} alignItems={"center"}>
                                            <Text fontSize="16px" fontWeight={"md"} pl={3}> {"$" + (isViewWithPath ?
                                                ((elem.finalPrice !== undefined ? elem.finalPrice : 0) * elem.quantity).toFixed(2) :
                                                Number((ProductCostCalculateUtils.calculatePrice(elem, selectedDealer) * elem.quantity).toFixed(2)))}
                                            </Text>
                                            <Box pr={1}>
                                                {isViewWithPath ?
                                                    <Checkbox marginLeft={"10px"} checked={selectedIds.includes(elem?.id)}
                                                              onChange={(event) => handleCheckboxChange(elem?.id, event.target.checked)}
                                                    /> :
                                                    <IconWithTooltip icon={CloseIcon} tooltipMessage={'Remove'}
                                                                     color={"#D52E34"}
                                                                     onClick={() => handleRemoveClick(elem)}/>
                                                }
                                            </Box>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            </Box>,
                        isLink: false,
                        width: "100%",
                        justifyContent: "flex-start"
                    }] :
                [
                    {
                        content: (<ImageCustom elem={elem} sizeInPercent={100} currentSize={"medium"}
                                               sizeBackend={"COMMON"}/>),
                        isLink: false,
                        width: "13%",
                        justifyContent: "flex-start"
                    },
                    {
                        content: (
                            <Box flexDir="column" textAlign="left" maxWidth={"650px"}>
                                <Text fontSize={16} fontWeight="md">{elem.name}</Text>
                                {/*<Text fontWeight="500" color="gray.400"> Product#: {elem.id} </Text>*/}
                                <Text fontSize={14} color="gray.400">{elem.description}</Text>
                                {elem.type === ProductTypes.CUSTOM && (
                                    <Text fontWeight="600" fontSize="16px">
                                        {'This product is custom and will be billed and shipped on separate invoice.'}
                                    </Text>
                                )}
                                {elem.type === ProductTypes.REORDER && (
                                    <Text fontWeight="600" fontSize="16px">
                                        {'This product will be billed and shipped on separate invoice.'}
                                    </Text>
                                )}
                            </Box>
                        ),
                        width: "40%",
                        justifyContent: "flex-start"
                    },
                    {
                        content: <DiscountComponent elem={elem} hiddenUOM={true} history={isViewWithPath}
                                                    selectedDealer={selectedDealer}
                                                    showDiscount={!isViewWithPath}
                                                    showTax={!isViewWithPath && hasPermission(orderEditPermission)}
                                                    showMarkup={!isViewWithPath && hasPermission(orderEditPermission)}
                        />,
                    },
                    {
                        content: isViewWithPath ?
                            <Text fontSize="14px">{elem.quantity}</Text> :
                            <QuantityInput item={elem} isViewWithPath={isViewWithPath}
                                           onQuantityRemove={(updatedItem) => {
                                               handleRemoveClick(updatedItem);
                                           }}
                                           onQuantityChange={(updatedItem) => {
                                               const updatedProduct = cartItems.find((item) => item.id === elem.id);
                                               if (updatedProduct) {
                                                   updatedProduct.quantity = updatedItem.quantity;
                                                   cartService.addProductToCart(updatedProduct);
                                               }
                                           }}/>,
                        width: "15%"
                    },
                    {
                        content: <Text
                            fontSize="14px"> {"$" + (isViewWithPath ?
                            ((elem.finalPrice !== undefined ? elem.finalPrice : 0) * elem.quantity).toFixed(2) :
                            Number((ProductCostCalculateUtils.calculatePrice(elem, selectedDealer) * elem.quantity).toFixed(2)))}
                        </Text>,
                        width: "5%"
                    },
                    hasPermission(orderEditPermission) && {
                        content: <Text
                            fontSize="14px"> {isViewWithPath ? elem.shippingCost.toFixed(2) : (
                            (elem.parentShippingCost !== 0 && elem.parentType === ProductTypes.OPTIONS) ?
                                `SumQnt ${ProductCostCalculateUtils.getCountQuantityOptions(updatedProductList, elem)}` :
                                ProductCostCalculateUtils.calculateShippingCost(elem, selectedDealer).toFixed(2) + "$")}</Text>,
                        width: "10%"
                    },
                    hasPermission(orderEditPermission) && {
                        content: <Text
                            fontSize="14px">{isViewWithPath && elem.taxCost ? elem.taxCost.toFixed(2) : ProductCostCalculateUtils.calculateTaxCost(elem, selectedDealer).toFixed(2) + "$"}</Text>,
                        width: "10%"
                    },
                    !isUnRegistered && {
                        content: isViewWithPath ? <Checkbox marginLeft={"10px"} checked={selectedIds.includes(elem?.id)}
                                                            onChange={(event) => handleCheckboxChange(elem?.id, event.target.checked)}
                        /> : <IconWithTooltip icon={TrashItem} tooltipMessage={'Remove'}
                                              onClick={() => handleRemoveClick(elem)}/>,
                        width: "auto"
                    },
                    // hasPermission(scheduledShipmentCreatePermission) && {
                    //     content: <AddScheduledShipmentPopover
                    //         onSuccess={() => setRefresh(prevIndex => !prevIndex)}
                    //         item={shipment ? {...shipment.product, quantity: shipment.quantity} : elem}
                    //         editItem={shipment ? shipment : undefined}
                    //         dealer={{
                    //             id: selectedDealer?.id ?? 0,
                    //             name: selectedDealer?.name ?? "Unknown"
                    //         }}
                    //     />,
                    //     width: "auto"
                    // }
                ].filter(Boolean) as Array<ListProductItemConfig>;
            return <ListProductItemRow key={idx} list={ItemConfig} noBorder={false}
                                       style={{p: "2px 0px"}}/>;
        });
    }

    const dealershipOptions = useMemo<DealershipMinInfo[]>(() => {
        if (hasPermission(dealershipExcludeSelectPermission)) {
            return (currentUser?.dealerships || [])
                .filter((d): d is DealershipMinInfo => typeof d.id === "number" && !!d.name)
                .map((d) => ({id: d.id, name: d.name}));
        }
        return dealershipsMinInfo || [];
    }, [currentUser?.dealerships, dealershipsMinInfo]);

    const filteredDealershipOptions = useMemo(() => {
        const query = dealershipInputValue.trim().toLowerCase();
        const source = dealershipOptions.filter((d) => d.name);
        if (!query) {
            return source.slice(0, 100);
        }
        return source.filter((d) => d.name.toLowerCase().includes(query)).slice(0, 100);
    }, [dealershipOptions, dealershipInputValue]);

    const updateDealershipDropdownPosition = useCallback(() => {
        const el = dealershipSelectRef.current;
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const gap = 8;
        if (spaceAbove > spaceBelow) {
            setDealershipDropdownPlacement("up");
            setDealershipDropdownMaxH(`${Math.max(120, spaceAbove - gap)}px`);
        } else {
            setDealershipDropdownPlacement("down");
            setDealershipDropdownMaxH(`${Math.max(120, spaceBelow - gap)}px`);
        }
    }, []);

    const openDealershipDropdown = useCallback(() => {
        updateDealershipDropdownPosition();
        setIsDealershipDropdownOpen(true);
    }, [updateDealershipDropdownPosition]);

    const handleDealershipSelect = (dealership: DealershipMinInfo) => {
        setDealershipInputValue(dealership.name);
        setIsDealershipDropdownOpen(false);
        setUpdateDealer(true);
        userController.getDealership({id: dealership.id});
    };

    useEffect(() => {
        if (!isDealershipDropdownOpen) {
            return;
        }
        const handleReposition = () => updateDealershipDropdownPosition();
        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, true);
        return () => {
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, true);
        };
    }, [isDealershipDropdownOpen, updateDealershipDropdownPosition]);

    const orderUpdate = (id: any, totalAmount: number, shippingAmount: number) => {
        console.log("updating order")

        modalService.setModalData({
            onSubmit: () => {
                modalService.deleteModalData();
                orderController.orderUpdate({
                    data: {
                        id: id,
                        totalAmount: totalAmount,
                        shippingAmount: shippingAmount
                    }
                }).then(() => {
                });
            },
            isOpen: true,
            onOpen: () => {
            },
            onClose: () => {
                modalService.deleteModalData();
            },
            modalTextData: `Are you sure that you want to change ${id} order ?`,
            context: "action"
        });
    };
    return (
        <>
            {!account &&
                <Breadcrumbs/>
            }
            {isLoading ? <Loader/> :
                <Box height={"100%"} p={{base: 0, lg: 0}}>
                    <Box {...BoxStyleConfig}>
                        {isUnRegistered &&
                            <Box w={"50%"} marginX={"auto"} py={6} px={10} background={"#E9F5D9"} borderRadius={"5px"}>
                                <Text fontWeight={"md"} fontSize={16} color={"#457B00"}>Thank you for your
                                    information!</Text>
                                <Text fontWeight={"sm"} fontSize={16} color={"#457B00"} pt={4}>
                                    Registering a new customer usually takes 30 min to an hour during working hours.
                                    Once approved, you will receive your login password via email. Your order will be
                                    completed with correct taxes if applicable and shipping cost, also it will be saved
                                    in your account.</Text>
                            </Box>
                        }
                        {isViewWithPath &&
                            <>
                                <Text>
                                    DATE: {dateOrder}
                                </Text>
                                {(paymentState !== OrderTypes.IN || hasPermission(orderEditPermission)) &&
                                    <Text
                                        mb="20px">
                                        ORDER#: {id}
                                    </Text>
                                }
                            </>
                        }

                        <ListProductItemRow list={ColumnNamesMapping} noBorder={false} style={{
                            fontSize: "14px",
                            minHeight: "35px",
                            justifyContent: "flex-start",
                            paddingBottom: isMobile ? 3 : 0,
                            paddingTop: isMobile ? 3 : 0
                        }}/>
                        {getList(updatedProductList)}
                        {(!isUnRegistered && selectedDealer) &&
                            <Box width="100%" padding={"10px 0"} borderBottom={"none"}>
                                <Flex justifyContent="space-between" alignItems="flex-start" w="100%" maxW="100%">
                                    <Box
                                        {...BoxStyleConfig}
                                        flex={1}
                                        minW={0}
                                        w="100%"
                                        maxW="100%"
                                        alignSelf="stretch"
                                        alignItems="stretch"
                                    >
                                        {hasPermission(orderEditPermission) && !isViewWithPath ?
                                            <Text
                                                ml="auto">
                                                TAX:{
                                                `${ProductCostCalculateUtils.getTaxShow(selectedDealer)} $${tax.toFixed(2)}`}
                                            </Text>
                                            :
                                            <Text
                                                ml="auto">
                                                TAX: ${tax.toFixed(2)}
                                            </Text>
                                        }
                                        {hasPermission(orderEditPermission) ?
                                            <Box display="flex" flexDir="row"
                                                 justifyContent="flex-start"
                                                 alignSelf="flex-start"
                                                 ml="auto" mb={"5px"}>

                                                {shippingEdit ?
                                                    <Text ml="auto">
                                                        Shipping: ${shippingCost.toFixed(2)}
                                                    </Text>
                                                    :
                                                    <>
                                                        <Text
                                                            mr={"10px"}>Shipping:</Text>
                                                        <Input
                                                            maxWidth={"120px"}
                                                            height="30px"
                                                            borderRadius="2px"
                                                            defaultValue={shippingCost}
                                                            onChange={(e) => {
                                                                setShippingCost(Number(e.target.value));
                                                                if (isViewWithPath) {
                                                                    setTotalPrice(((order?.totalAmount || 0) - (order?.shippingCost || 0)) + Number(e.target.value));
                                                                } else {
                                                                    setTotalPrice(subTotal + tax + Number(e.target.value));
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    if (isViewWithPath) {
                                                                        orderUpdate(id, totalPrice, shippingCost);
                                                                    }
                                                                    setShippingEdit(true)
                                                                }
                                                            }}
                                                            // isDisabled={isViewWithPath}
                                                        />
                                                    </>
                                                }

                                                {((isViewWithPath && order?.tbd) || !isViewWithPath) && (shippingEdit ?
                                                    <Icon
                                                        ml={"10px"}
                                                        as={FiEdit}
                                                        fontSize={"22px"}
                                                        strokeWidth={0.75}
                                                        cursor={"pointer"}
                                                        onClick={() => setShippingEdit(false)}
                                                    />
                                                    :
                                                    <Icon
                                                        ml={"10px"}
                                                        as={FiCheck}
                                                        fontSize={"22px"}
                                                        strokeWidth={1}
                                                        cursor={"pointer"}
                                                        onClick={() => {
                                                            setShippingEdit(true)
                                                            if (isViewWithPath) {
                                                                orderUpdate(id, totalPrice, shippingCost);
                                                            }
                                                        }}
                                                        _hover={{
                                                            background: "gray.100"
                                                        }}
                                                    />)
                                                }
                                            </Box>
                                            :
                                            <Text ml="auto">
                                                <span>Shipping:</span>
                                                <span style={{
                                                    paddingLeft: 4,
                                                    color: isTBD ? "purple" : "unset",
                                                    fontWeight: isTBD ? "600" : "unset"
                                                }}>{isTBD ? "TBD" : "$" + shippingCost.toFixed(2)}</span>
                                            </Text>
                                        }
                                        {hasPermission(orderEditPermission) ?
                                            <Text ml="auto">
                                                Total: {`${totalPrice.toFixed(2)}`}
                                            </Text>
                                            :
                                            <Text ml="auto">
                                                <span>Total:</span>
                                                <span style={{
                                                    paddingLeft: 4,
                                                    color: isTBD ? "purple" : "unset",
                                                    fontWeight: isTBD ? "600" : "unset"
                                                }}>{isTBD ? "TBD" : "$" + totalPrice.toFixed(2)}</span>
                                            </Text>
                                        }
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="flex-end"
                                            w="100%"
                                            maxW="100%"
                                            gap={1}
                                        >
                                        <Box display="flex" flexDir="row" justifyContent="flex-start"
                                             alignItems="center"
                                             flexWrap={{base: "wrap", md: "nowrap"}}
                                             gap={1}>
                                            <Text mr={{md: "10px"}} whiteSpace="nowrap">PO#</Text>
                                            <Box
                                                flexShrink={0}
                                                minW="max(120px, 11ch)"
                                                maxW="min(100%, 20ch)"
                                                w={`${poFieldSizingCh}ch`}
                                                sx={{boxSizing: "border-box"}}
                                            >
                                                <Input
                                                    w="100%"
                                                    minW={0}
                                                    overflowX="auto"
                                                    fontSize={14}
                                                    h="30px"
                                                    lineHeight="30px"
                                                    py={0}
                                                    px={2}
                                                    borderRadius="2px"
                                                    value={poNumber}
                                                    onChange={(e) => {
                                                        setPoNumber(e.target.value)
                                                    }}
                                                    placeholder="OPTIONAL"
                                                    isDisabled={isViewWithPath}
                                                />
                                            </Box>
                                        </Box>
                                        <Box display="flex" flexDir="column"
                                             alignItems="flex-end"
                                             maxW={{base: "100%", md: "min(100%, 42rem)"}} gap={1}>
                                            <Box display="flex" flexDir="row" justifyContent="flex-start"
                                                 alignItems="center" flexWrap={{base: "wrap", md: "nowrap"}}
                                                 gap={1} w="auto" maxW="100%">
                                                <Text mr={{md: "10px"}} whiteSpace="nowrap">RO or Orderer’s Name</Text>
                                                <Box
                                                    flexShrink={0}
                                                    minW="max(120px, 11ch)"
                                                    maxW="min(100%, 35ch)"
                                                    w={`${roFieldSizingCh}ch`}
                                                    sx={{boxSizing: "border-box"}}
                                                >
                                                    <Input
                                                        w="100%"
                                                        minW={0}
                                                        overflowX="auto"
                                                        fontSize={14}
                                                        h="30px"
                                                        lineHeight="30px"
                                                        py={0}
                                                        px={2}
                                                        borderRadius="2px"
                                                        value={namePo}
                                                        onChange={(e) => {
                                                            setNamePo(e.target.value)
                                                        }}
                                                        borderColor={
                                                            (!isViewWithPath && cartNamePoVins.length > 0 && roVinConflictOrderIds.length > 0 && !roVinAckedForCurrentPo)
                                                                ? "red.500"
                                                                : (!(hasPermission(orderEditPermission) || hasPermission(dealershipExcludeSelectPermission)) && namePo === "" && !isViewWithPath)
                                                                    ? "red.500"
                                                                    : "gray.300"
                                                        }
                                                        placeholder={cartRoPlaceholderForWidth}
                                                        isDisabled={isViewWithPath}
                                                    />
                                                </Box>
                                            </Box>
                                            {!isViewWithPath && cartNamePoVins.length > 0 && (
                                                <Text fontSize="xs"
                                                      color={roVinConflictOrderIds.length > 0 && !roVinAckedForCurrentPo ? "red.500" : "gray.500"}>
                                                    {roVinCheckLoading
                                                        ? "Checking VIN (last 8 characters)…"
                                                        : roVinConflictOrderIds.length > 0 && !roVinAckedForCurrentPo
                                                            ? `Matches recent orders (IDs: ${roVinConflictOrderIds.join(", ")}). Confirm in the dialog or change the RO field.`
                                                            : roVinAckedForCurrentPo && roVinConflictOrderIds.length > 0
                                                                ? "Duplicate VIN acknowledged for this RO."
                                                                : "VIN pattern detected; no conflict with recent orders."}
                                                </Text>
                                            )}
                                        </Box>
                                        {hasPermission(orderEditPermission) && ((confirmNumber && isViewWithPath) || (!isViewWithPath && selectedDealer.dealerLicenseNumber)) &&
                                            <Box display="flex" flexDir="row" justifyContent="flex-start"
                                                 alignItems="center">
                                                <Text mr={"10px"}>NJ Paper Order#</Text>
                                                {isViewWithPath ? (
                                                    <>
                                                        <Text>{confirmNumber}</Text>
                                                        <OrderPaperPopover/>
                                                        {paperOrder?.status === 'Submitted' && id && (
                                                            <IconWithTooltip icon={FaWindowClose} onClick={() => {
                                                                orderController.chancelOrderPaper(Number(id)).then((res) => {
                                                                    dispatch({type: "CLEAR_PAPER_ORDER"});
                                                                });
                                                            }} tooltipMessage={'Cancel Paper Order'}/>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Input
                                                        fontSize={14}
                                                        w="120px"
                                                        minW="120px"
                                                        maxW="120px"
                                                        h="30px"
                                                        borderRadius="2px"
                                                        value={confirmNumber}
                                                        placeholder='OPTIONAL'
                                                        onChange={(e) => {
                                                            setConfirmNumber(e.target.value);
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                            <Box
                                            w={{base: "100%", lg: "auto"}}
                                            maxW={{base: "100%", lg: "min(100%, 36rem)"}}
                                            pt={3}
                                            alignSelf={{base: "stretch", lg: "flex-end"}}
                                        >
                                        <Box
                                            display="flex"
                                            flexDir={{base: "column", lg: "row"}}
                                            justifyContent="flex-start"
                                            alignItems={{base: "stretch", lg: "flex-start"}}
                                            gap={{base: 4, lg: 6}}
                                            w={{base: "100%", lg: "auto"}}
                                            maxW="100%"
                                            flexShrink={0}
                                        >
                                            {(hasPermission(orderEditPermission) || hasPermission(dealershipExcludeSelectPermission)) && !isViewWithPath && (
                                                <Box
                                                    ref={dealershipSelectRef}
                                                    position="relative"
                                                    w={{base: "100%", lg: "17.25rem"}}
                                                    maxW={{base: "100%", lg: "17.25rem"}}
                                                    minW={0}
                                                    mr={{base: 0, lg: 2}}
                                                    flex={{base: "1 1 auto", lg: "0 0 auto"}}
                                                >
                                                    <Input
                                                        w="100%"
                                                        maxW="100%"
                                                        h="40px"
                                                        minH="40px"
                                                        size="sm"
                                                        borderRadius="md"
                                                        fontSize="14px"
                                                        fontWeight="sm"
                                                        borderColor="gray.200"
                                                        bg="white"
                                                        pr={1}
                                                        placeholder="Dealership"
                                                        value={dealershipInputValue}
                                                        onFocus={openDealershipDropdown}
                                                        onChange={(e) => {
                                                            setDealershipInputValue(e.target.value);
                                                            openDealershipDropdown();
                                                        }}
                                                        onBlur={() => {
                                                            setTimeout(() => setIsDealershipDropdownOpen(false), 120);
                                                        }}
                                                        _hover={{borderColor: "gray.300"}}
                                                        _focusVisible={{
                                                            borderColor: "blue.400",
                                                            boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                                                        }}
                                                    />
                                                    {dealershipInputValue && (
                                                        <IconButton
                                                            aria-label="Clear dealership"
                                                            size="xs"
                                                            variant="ghost"
                                                            position="absolute"
                                                            right="4px"
                                                            top="50%"
                                                            transform="translateY(-50%)"
                                                            minW="24px"
                                                            h="24px"
                                                            zIndex={1}
                                                            icon={<Icon as={FiX} boxSize={4} strokeWidth={5} color="gray.300" _hover={{color: "gray.500"}}/>}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => {
                                                                setDealershipInputValue("");
                                                                setIsDealershipDropdownOpen(false);
                                                            }}
                                                        />
                                                    )}
                                                    {isDealershipDropdownOpen && filteredDealershipOptions.length > 0 && (
                                                        <Box
                                                            position="absolute"
                                                            {...(dealershipDropdownPlacement === "up"
                                                                ? {bottom: "calc(100% + 4px)"}
                                                                : {top: "calc(100% + 4px)"})}
                                                            left={0}
                                                            right={0}
                                                            zIndex={20}
                                                            bg="white"
                                                            borderWidth="1px"
                                                            borderColor="gray.200"
                                                            borderRadius="md"
                                                            boxShadow="md"
                                                            maxH={dealershipDropdownMaxH}
                                                            overflowY="auto"
                                                        >
                                                            {filteredDealershipOptions.map((elem) => (
                                                                <Box
                                                                    key={elem.id}
                                                                    px={2}
                                                                    py={1.5}
                                                                    fontSize="14px"
                                                                    fontWeight="400"
                                                                    cursor="pointer"
                                                                    _hover={{bg: "gray.50"}}
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        handleDealershipSelect(elem);
                                                                    }}
                                                                >
                                                                    {elem.name}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                            <Box
                                                w={{base: "100%", lg: "auto"}}
                                                minW={0}
                                                flex={{base: "1 1 auto", lg: "0 0 auto"}}
                                            >
                                                {isMobile && !isRoleAdmin() &&
                                                    <>
                                                        <Box border={"0.7px solid #D1D5DB"} width={"100%"}/>
                                                        <Text pt={6}>{'Please check out and confirm your order'}</Text>
                                                    </>
                                                }
                                                <Flex w={{base: "100%", lg: "auto"}} pt={{base: 4, lg: 0}}
                                                      justify={{base: "stretch", lg: "flex-start"}}>
                                                    {!isViewWithPath && (
                                                        <VStack
                                                            w={{base: "100%", lg: "auto"}}
                                                            maxW={{base: "100%", lg: !isRoleAdmin()? "180px" : "210px"}}
                                                            spacing={4}
                                                            align="stretch"
                                                        >
                                                            {showInvoiceMeButton && (
                                                                <Button
                                                                    type={"button"}
                                                                    boxSizing={"border-box"}
                                                                    w={{base: "100%", lg: !isRoleAdmin()? "180px" : "210px"}}
                                                                    maxW={"100%"}
                                                                    h={"71px"}
                                                                    minH={"71px"}
                                                                    p={"15px 32px"}
                                                                    display={"flex"}
                                                                    flexDirection={"column"}
                                                                    alignItems={"center"}
                                                                    justifyContent={"center"}
                                                                    fontFamily={"Poppins, sans-serif"}
                                                                    background={"#F3F4F6"}
                                                                    borderWidth={!isRoleAdmin() ? "2.5px" : "1px"}
                                                                    borderColor={!isRoleAdmin() ? "gray.800" : "#D1D5DB"}
                                                                    borderRadius={"5px"}
                                                                    boxShadow={"none"}
                                                                    _hover={{
                                                                        background: "#E5E7EB",
                                                                    }}
                                                                    _active={{
                                                                        background: "#D1D5DB",
                                                                    }}
                                                                    _disabled={{
                                                                        opacity: 0.6,
                                                                        cursor: "not-allowed",
                                                                    }}
                                                                    isDisabled={
                                                                        updatedProductList.length === 0 ||
                                                                        roVinBlocksCheckout ||
                                                                        roRequiredButEmpty
                                                                    }
                                                                    onClick={() => handleCheckoutCart(totalPrice, shippingCost, tax)}
                                                                >
                                                                    <Text
                                                                        as={"span"}
                                                                        fontFamily={"'Poppins', sans-serif"}
                                                                        fontStyle={"normal"}
                                                                        fontWeight={600}
                                                                        fontSize={"16px"}
                                                                        lineHeight={"100%"}
                                                                        display={"flex"}
                                                                        alignItems={"center"}
                                                                        justifyContent={"center"}
                                                                        textAlign={"center"}
                                                                        color={"#1F2937"}
                                                                    >
                                                                        {!isRoleAdmin() ? 'Invoice Me' : 'Proceed to Checkout'}
                                                                    </Text>
                                                                </Button>
                                                            )}
                                                            {showInvoiceMeButton && showPayWithCard && (
                                                                <Text textAlign={"center"} fontSize={"sm"}
                                                                      color={"gray.500"}>
                                                                    OR
                                                                </Text>
                                                            )}
                                                            {showPayWithCard && (
                                                                <Button
                                                                    type={"button"}
                                                                    boxSizing={"border-box"}
                                                                    w={{base: "100%", lg: "246px"}}
                                                                    maxW={"100%"}
                                                                    h={"71px"}
                                                                    minH={"71px"}
                                                                    p={"15px 32px"}
                                                                    display={"flex"}
                                                                    flexDirection={"column"}
                                                                    alignItems={"center"}
                                                                    justifyContent={"center"}
                                                                    fontFamily={"Poppins, sans-serif"}
                                                                    background={"primary.gray.light"}
                                                                    color={"black"}
                                                                    borderWidth={"1px"}
                                                                    borderColor={"gray.200"}
                                                                    borderRadius={"5px"}
                                                                    boxShadow={"none"}
                                                                    _hover={{
                                                                        background: "#E5E7EB",
                                                                    }}
                                                                    _active={{
                                                                        background: "#D1D5DB",
                                                                    }}
                                                                    _disabled={{
                                                                        opacity: 0.6,
                                                                        cursor: "not-allowed",
                                                                    }}
                                                                    isDisabled={
                                                                        updatedProductList.length === 0 ||
                                                                        roVinBlocksCheckout ||
                                                                        roRequiredButEmpty ||
                                                                        cardCheckoutBlockedByTbd
                                                                    }
                                                                    onClick={handleCardPayment}
                                                                >
                                                                    <Flex
                                                                        direction={"column"}
                                                                        align={"center"}
                                                                        justify={"center"}
                                                                        gap={"4px"}
                                                                        w={"100%"}
                                                                        maxW={"170px"}
                                                                    >
                                                                        <Text
                                                                            as={"span"}
                                                                            fontFamily={"'Poppins', sans-serif"}
                                                                            fontStyle={"normal"}
                                                                            fontWeight={600}
                                                                            fontSize={"16px"}
                                                                            lineHeight={"100%"}
                                                                            display={"flex"}
                                                                            alignItems={"center"}
                                                                            justifyContent={"center"}
                                                                            textAlign={"center"}
                                                                            color={"#1F2937"}
                                                                        >
                                                                            Pay with Card
                                                                        </Text>
                                                                        <Text
                                                                            fontFamily={"'Poppins', sans-serif"}
                                                                            fontStyle={"normal"}
                                                                            fontWeight={400}
                                                                            fontSize={"14px"}
                                                                            lineHeight={"150%"}
                                                                            minH={"21px"}
                                                                            display={"flex"}
                                                                            alignItems={"center"}
                                                                            justifyContent={"center"}
                                                                            textAlign={"center"}
                                                                            color={"#4B5563"}
                                                                        >
                                                                            {`add (${getStripeCommissionLabel()})`}
                                                                        </Text>
                                                                    </Flex>
                                                                </Button>
                                                            )}
                                                        </VStack>
                                                    )}

                                                    {isViewWithPath && (
                                                        <MainButton ml={{lg: "auto"}} minWidth={"180px"} minH={"40px"}
                                                                    background={{
                                                                        base: "gray.800",
                                                                        lg: "primary.gray.light"
                                                                    }}
                                                                    color={{base: "white", lg: "black"}}
                                                                    disabled={selectedIds.length === 0}
                                                                    onClick={handleAddedSelectProductToCart}>
                                                            Add to cart
                                                        </MainButton>
                                                    )}
                                                </Flex>
                                            </Box>

                                        </Box>
                                        </Box>
                                        </Box>
                                    </Box>
                                </Flex>
                            </Box>
                        }
                    </Box>
                </Box>
            }
            {(!selectedDealer && !isUnRegistered && !account && !hasPermission(orderViewPermission)) &&
                <Flex pt={5} direction={{base: "column", lg: "row"}}>
                    <AuthorizationForm basket={true}/>
                    <RequestRegistrationForm isEditing={false} basket={true}/>
                </Flex>
            }

            <RoVinConflictModal
                isOpen={roVinModalOpen}
                onClose={handleRoVinModalClose}
                onContinue={handleRoVinModalContinue}
                conflicts={roVinConflicts}
                conflictOrderIds={roVinConflictOrderIds}
                conflictRows={roVinConflictRows}
            />

            <StripePaymentModal
                isOpen={showStripePayment}
                onClose={handleCancelPayment}
                paymentContext="cart"
                amount={totalPrice}
                currency="usd"
                stripeKey={stripeOrderId ?? "cart-checkout"}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                orderData={{
                    orderId: stripeOrderId,
                    poNumber: poNumber,
                    namePo: namePo,
                    dealershipId: selectedDealer?.id,
                    customerEmail: currentUser?.email,
                    customerName: currentUser?.login,
                    description: stripeOrderId ? `Payment for order #${stripeOrderId}` : "Payment for order",
                }}
            />
        </>
    );
}

export default OrderBasket;
